import { InfoKeyValue } from '@/components/info-key-value';
import { booleanToCustomString } from '@/lib/string-utils';
import { AssessmentContent, AssessmentResult } from '@airreading/common/data';

export const ReadOnlyAssessmentResultForm = ({
  result,
  sortedAssessmentContents,
  resultIndex,
}: {
  result: AssessmentResult;
  resultIndex: number;
  sortedAssessmentContents: AssessmentContent[];
  assessmentDisplayName: string;
}) => {
  const content = sortedAssessmentContents.find((c) => c.id === result.assessmentContentId);
  if (!content) {
    return <p>Assessment content {result.assessmentContentId} not found</p>;
  }

  const {
    incorrectWords,
    incorrectWordsCount,
    phonemicReadingResult,
    comprehensionSkillResult,
    comprehensionPassageResult,
    fluencySkillResult,
    totalWordsRead,
    canRetell,
    incorrectlWordsReadFromPMList,
    finishedEarly,
    totalSecondsSpent,
  } = result;
  const { format } = content;

  if (format === 'diagnostic_word_list') {
    if (incorrectWordsCount == null) {
      return null; // no feedback submitted yet
    }

    const currContentWords = sortedAssessmentContents[resultIndex].words;
    return incorrectWords.length === 0 ? (
      'All correct'
    ) : (
      <div>
        <p>Missed word(s)</p>
        <div>
          {incorrectWords
            .toSorted((a, b) => currContentWords.findIndex((w) => w === a) - currContentWords.findIndex((w) => w === b))
            .join(', ')}
        </div>
      </div>
    );
  }

  if (format === 'mastery_check_reading_fluency_passage') {
    return fluencySkillResult;
  }

  if (format === 'mastery_check_reading_fluency_passage_v2') {
    return fluencySkillResult;
  }

  if (format === 'mastery_check_phonemic_reading') {
    return booleanToCustomString(phonemicReadingResult);
  }

  if (format === 'mastery_check_comprehesion_passage') {
    return booleanToCustomString(comprehensionPassageResult);
  }

  if (format === 'mastery_check_reading') {
    return incorrectWordsCount == null ? null : `Missed ${incorrectWordsCount} word(s)`;
  }

  if (format === 'mastery_check_comprehesion_skill') {
    return comprehensionSkillResult;
  }

  if (format === 'mastery_check_skill_preview') {
    return incorrectWordsCount == null ? null : `Missed ${incorrectWordsCount} word(s)`;
  }

  if (format === 'grade_level_fluency') {
    return (
      <div className="flex flex-wrap gap-8 w-full">
        <InfoKeyValue label={'Number of words read'} value={totalWordsRead} />
        <InfoKeyValue label={'Number of mistakes'} value={incorrectWordsCount} />
        <InfoKeyValue label={'Is the student able to retell?'} value={booleanToCustomString(canRetell)} />
      </div>
    );
  }

  if (format === 'progress_monitor_sounds') {
    return (
      <div className="flex flex-wrap gap-8 w-full">
        <InfoKeyValue label={'Number of letter sounds read'} value={totalWordsRead} />
        <InfoKeyValue label={'Number of mistakes'} value={incorrectWordsCount} />
        <InfoKeyValue label={'Number of words read incorrectly from the list'} value={incorrectlWordsReadFromPMList} />
      </div>
    );
  }

  if (format === 'progress_monitor_words') {
    return (
      <div className="flex flex-wrap gap-8 w-full">
        <InfoKeyValue label={'Number of words read'} value={totalWordsRead} />
        <InfoKeyValue label={'Number of mistakes'} value={incorrectWordsCount} />
        <InfoKeyValue label={'Number of words read incorrectly from the list'} value={incorrectlWordsReadFromPMList} />
        <InfoKeyValue label={'Student finished reading early'} value={booleanToCustomString(finishedEarly)} />
        {finishedEarly && (
          <InfoKeyValue
            label={
              <>
                How many seconds did the student use in <strong>total</strong>?
              </>
            }
            value={totalSecondsSpent ?? 'N/A'}
          />
        )}
        <InfoKeyValue label={'Is the student able to retell?'} value={booleanToCustomString(canRetell)} />
      </div>
    );
  }

  if (format === 'progress_monitor_text') {
    return (
      <div className="flex flex-wrap gap-8 w-full">
        <InfoKeyValue label={'Number of words read'} value={totalWordsRead} />
        <InfoKeyValue label={'Number of mistakes'} value={incorrectWordsCount} />
        <InfoKeyValue label={'Student finished reading early'} value={booleanToCustomString(finishedEarly)} />
        {finishedEarly && (
          <InfoKeyValue
            label={
              <>
                How many seconds did the student use in <strong>total</strong>?
              </>
            }
            value={totalSecondsSpent ?? 'N/A'}
          />
        )}
      </div>
    );
  }

  return <>{content.format} display not implemented</>;
};
